import { useEffect, useState } from "react";
import { Minus, Plus, Truck, CheckCircle2, ZoomIn, Layers } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, img, tl, catLabel } from "@/lib/api";
import { useCart } from "@/context/CartContext";

function Zoom({ src, alt }) {
  const [pos, setPos] = useState(null);
  const move = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setPos({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
  };
  return (
    <div data-testid="product-detail-zoom" onMouseMove={move} onMouseLeave={() => setPos(null)}
      className="relative aspect-square bg-white rounded-lg border border-slate-200 overflow-hidden cursor-zoom-in">
      <img src={src} alt={alt} className="h-full w-full object-contain transition-transform duration-200"
        style={pos ? { transform: "scale(2.2)", transformOrigin: `${pos.x}% ${pos.y}%` } : undefined} />
      {!pos && <span className="absolute bottom-3 right-3 flex items-center gap-1 text-xs bg-white/90 border px-2 py-1 rounded"><ZoomIn className="h-3.5 w-3.5" />Büyütmek için üzerine gelin</span>}
    </div>
  );
}

export default function ProductDialog({ product, onClose }) {
  const { add } = useCart();
  const [qty, setQty] = useState(1);
  const [main, setMain] = useState(null);

  useEffect(() => {
    if (product) {
      setQty(1);
      setMain(product.image);
      api.post(`/products/${product.id}/view`).catch(() => {});
    }
  }, [product]);

  if (!product) return null;
  const p = product;
  const gallery = [p.image, ...(p.images || [])].filter(Boolean);
  const unit = p.bulk_price && qty >= p.bulk_min_qty ? p.bulk_price : p.sale_price;

  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="product-detail-dialog" className="max-w-5xl max-h-[92vh] overflow-y-auto p-0">
        <div className="grid md:grid-cols-2 gap-6 p-5 sm:p-8">
          <div>
            <Zoom src={img(main)} alt={p.name} />
            {gallery.length > 1 && (
              <div className="flex gap-2 mt-3 overflow-x-auto no-scrollbar">
                {gallery.map((g) => (
                  <button key={g} onClick={() => setMain(g)} className={`h-16 w-16 shrink-0 rounded border ${g === main ? "border-[#172554]" : "border-slate-200"}`}>
                    <img src={img(g)} alt="" className="h-full w-full object-contain" />
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="flex flex-col">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500">{p.brand} · {catLabel(p.category)} · {p.sub_type}</div>
            <DialogTitle className="text-2xl sm:text-3xl font-bold text-[#172554] mt-1">{p.name}</DialogTitle>
            <DialogDescription className="text-slate-600 mt-3 text-sm leading-relaxed">{p.description}</DialogDescription>
            <div className="mt-4 grid grid-cols-3 gap-2 text-center">
              {[["Ağırlık", p.weight], ["Çeşit", p.flavor], ["Stok", p.stock > 0 ? `${p.stock} adet` : "Yok"]].map(([k, v]) => (
                <div key={k} className="rounded-md border border-slate-200 bg-slate-50 py-2">
                  <div className="text-[11px] text-slate-500">{k}</div>
                  <div className="text-sm font-bold">{v || "-"}</div>
                </div>
              ))}
            </div>
            {p.features?.length > 0 && (
              <ul className="mt-4 space-y-1.5" data-testid="product-detail-features">
                {p.features.map((f) => <li key={f} className="flex gap-2 text-sm"><CheckCircle2 className="h-4 w-4 text-[#16A34A] mt-0.5 shrink-0" />{f}</li>)}
              </ul>
            )}
            <div className="mt-5 flex items-end gap-3 flex-wrap">
              {p.discount_pct > 0 && <span className="bg-[#16A34A] text-white text-sm font-bold px-2 py-0.5 rounded">-%{Math.round(p.discount_pct)}</span>}
              <span data-testid="product-detail-price" className="text-3xl font-extrabold text-[#15803D]">{tl(unit)}</span>
              {p.old_price && <span className="text-slate-400 line-through">{tl(p.old_price)}</span>}
            </div>
            {p.bulk_price && (
              <div className="mt-2 flex items-center gap-2 text-sm font-semibold text-[#172554] bg-blue-50 border border-blue-100 rounded-md px-3 py-2">
                <Layers className="h-4 w-4" /> Toplu alım: {p.bulk_min_qty} adet ve üzeri adet başı {tl(p.bulk_price)}
              </div>
            )}
            {p.cargo_included && <div className="mt-2 flex items-center gap-2 text-sm font-semibold text-[#15803D]"><Truck className="h-4 w-4" />Kargo bizden</div>}
            <div className="mt-6 flex gap-3">
              <div className="flex items-center border border-slate-300 rounded-md">
                <button data-testid="product-detail-qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="h-12 w-10 flex items-center justify-center"><Minus className="h-4 w-4" /></button>
                <span data-testid="product-detail-qty" className="w-10 text-center font-bold">{qty}</span>
                <button data-testid="product-detail-qty-plus" onClick={() => setQty(Math.min(p.stock, qty + 1))} className="h-12 w-10 flex items-center justify-center"><Plus className="h-4 w-4" /></button>
              </div>
              <button data-testid="product-detail-add-button" disabled={p.stock <= 0} onClick={() => { add(p, qty); onClose(); }}
                className="flex-1 h-12 rounded-md bg-[#16A34A] hover:bg-[#15803D] text-white font-bold transition-colors disabled:bg-slate-300">
                {p.stock <= 0 ? "Stokta yok" : `Sepete Ekle · ${tl(unit * qty)}`}
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
